import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Database,
  AlertTriangle,
  AlertOctagon,
  Scale,
  Copy,
  ChevronRight,
  TrendingUp,
  Clock,
  FileText,
  ArrowUpRight,
} from 'lucide-react';
import { api } from '../services/api';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton';
import { Alert } from '../components/ui/Alert';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function Dashboard() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: api.getDashboardStats,
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="rectangular" width={120} height={40} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Connection Error">
        Failed to load dashboard data. Make sure the API is running and accessible.
      </Alert>
    );
  }

  const hasActiveConflicts = (stats?.summary.activeConflicts || 0) > 0;

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Entity resolution overview and system status</p>
        </div>
        <Link to="/ingestion">
          <Button leftIcon={<FileText className="h-4 w-4" />}>
            Upload Data
          </Button>
        </Link>
      </motion.div>

      {/* Priority Alert Banner */}
      {hasActiveConflicts && (
        <motion.div variants={item}>
          <Link to="/conflicts">
            <Alert variant="danger" title="DAP Conflicts Require Attention">
              <div className="flex items-center justify-between">
                <span>
                  {stats?.summary.activeConflicts} active conflict{stats?.summary.activeConflicts !== 1 ? 's' : ''} detected.
                  Review patron matches against exclusion lists immediately.
                </span>
                <ChevronRight className="h-5 w-5 shrink-0" />
              </div>
            </Alert>
          </Link>
        </motion.div>
      )}

      {/* Summary Cards */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Records"
          value={stats?.summary.totalRecords.toLocaleString() || '0'}
          icon={<Database className="h-6 w-6" />}
          variant="info"
          trend={{ value: 12, label: 'vs last week' }}
        />
        <StatCard
          title="Resolved Entities"
          value={stats?.summary.totalEntities.toLocaleString() || '0'}
          icon={<Users className="h-6 w-6" />}
          variant="success"
        />
        <StatCard
          title="Duplicates Found"
          value={stats?.summary.duplicatesFound.toLocaleString() || '0'}
          subtitle={`${stats?.summary.duplicateRate || 0}% duplicate rate`}
          icon={<Copy className="h-6 w-6" />}
          variant="warning"
        />
        <StatCard
          title="Active Conflicts"
          value={stats?.summary.activeConflicts.toString() || '0'}
          icon={<AlertTriangle className="h-6 w-6" />}
          variant="danger"
          highlight={hasActiveConflicts}
        />
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={item}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickActionCard
            title="DAP Conflicts"
            description="Review patron matches against exclusion lists"
            icon={<AlertOctagon className="h-6 w-6" />}
            href="/conflicts"
            variant="danger"
            badge={stats?.summary.activeConflicts || 0}
          />
          <QuickActionCard
            title="Review Matches"
            description="Adjudicate potential entity matches"
            icon={<Scale className="h-6 w-6" />}
            href="/adjudication"
            variant="warning"
          />
          <QuickActionCard
            title="Duplicate Accounts"
            description="Find patrons with multiple loyalty accounts"
            icon={<Copy className="h-6 w-6" />}
            href="/entities?dapOnly=true"
            variant="info"
            badge={stats?.summary.duplicatesFound || 0}
          />
        </div>
      </motion.div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Status */}
        <motion.div variants={item}>
          <Card>
            <CardHeader title="System Status" description="Real-time health monitoring" />
            <CardContent>
              <div className="space-y-4">
                <StatusIndicator
                  label="MERLIN Engine"
                  status={stats?.systemStatus.senzingApiHealthy || false}
                />
                <StatusIndicator
                  label="Database Connection"
                  status={stats?.systemStatus.databaseConnected || false}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Records by Source */}
        <motion.div variants={item}>
          <Card>
            <CardHeader title="Records by Data Source" description="Distribution across sources" />
            <CardContent>
              {stats?.recordsBySource && Object.keys(stats.recordsBySource).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.recordsBySource).map(([source, count]) => {
                    const total = stats.summary.totalRecords || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={source} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">{source}</span>
                          <span className="text-gray-500 dark:text-gray-400">
                            {count.toLocaleString()} ({percentage}%)
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="h-full bg-primary-500 rounded-full"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No data sources loaded yet.</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Recent Jobs */}
      <motion.div variants={item}>
        <Card>
          <CardHeader
            title="Recent Ingestion Jobs"
            description="Latest data processing activities"
            action={
              <Link to="/ingestion">
                <Button variant="ghost" size="sm" rightIcon={<ArrowUpRight className="h-4 w-4" />}>
                  View All
                </Button>
              </Link>
            }
          />
          <CardContent>
            {stats?.recentJobs && stats.recentJobs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">File</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Records</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {stats.recentJobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{job.filename}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="default">{job.dataSource}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <JobStatusBadge status={job.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {job.successCount.toLocaleString()}
                          {job.errorCount > 0 && (
                            <span className="text-red-600 dark:text-red-400 ml-1">({job.errorCount} errors)</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Clock className="h-4 w-4" />
                            {new Date(job.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Database className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No ingestion jobs yet.</p>
                <Link to="/ingestion">
                  <Button variant="secondary" className="mt-3">
                    Upload Your First File
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  variant: 'info' | 'success' | 'warning' | 'danger';
  trend?: { value: number; label?: string };
  highlight?: boolean;
}

function StatCard({ title, value, subtitle, icon, variant, trend, highlight }: StatCardProps) {
  const variantStyles = {
    info: {
      icon: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      highlight: 'ring-2 ring-blue-500',
    },
    success: {
      icon: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      highlight: 'ring-2 ring-green-500',
    },
    warning: {
      icon: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      highlight: 'ring-2 ring-amber-500',
    },
    danger: {
      icon: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      highlight: 'ring-2 ring-red-500 animate-pulse',
    },
  };

  const styles = variantStyles[variant];

  return (
    <Card className={highlight ? styles.highlight : ''}>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            {(subtitle || trend) && (
              <div className="mt-2 flex items-center gap-2">
                {trend && (
                  <span className="flex items-center gap-1 text-sm font-medium text-green-600 dark:text-green-400">
                    <TrendingUp className="h-4 w-4" />
                    {Math.abs(trend.value)}%
                  </span>
                )}
                {subtitle && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</span>
                )}
                {trend?.label && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{trend.label}</span>
                )}
              </div>
            )}
          </div>
          <div className={`rounded-xl p-3 ${styles.icon}`}>
            {icon}
          </div>
        </div>
      </div>
    </Card>
  );
}

function StatusIndicator({ label, status }: { label: string; status: boolean }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`h-3 w-3 rounded-full ${status ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <Badge variant={status ? 'success' : 'danger'} dot>
        {status ? 'Healthy' : 'Unavailable'}
      </Badge>
    </div>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'success' | 'info' | 'default' | 'danger'> = {
    completed: 'success',
    processing: 'info',
    uploaded: 'default',
    failed: 'danger',
  };

  return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
}

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  variant: 'danger' | 'warning' | 'info' | 'success';
  badge?: number;
}

function QuickActionCard({ title, description, icon, href, variant, badge }: QuickActionCardProps) {
  const variantStyles = {
    danger: {
      bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30',
      icon: 'text-red-600 dark:text-red-400',
      badge: 'bg-red-600',
    },
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30',
      icon: 'text-amber-600 dark:text-amber-400',
      badge: 'bg-amber-600',
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30',
      icon: 'text-blue-600 dark:text-blue-400',
      badge: 'bg-blue-600',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30',
      icon: 'text-green-600 dark:text-green-400',
      badge: 'bg-green-600',
    },
  };

  const styles = variantStyles[variant];

  return (
    <Link
      to={href}
      className={`block p-5 rounded-xl border transition-all duration-200 group ${styles.bg}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={styles.icon}>{icon}</div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
              {badge !== undefined && badge > 0 && (
                <span className={`${styles.badge} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}
